import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CopyOutlined, CheckOutlined, MenuOutlined, CloseOutlined,
  RocketOutlined, CodeOutlined, AppstoreOutlined, BgColorsOutlined,
  FileTextOutlined, KeyOutlined, ApiOutlined, DollarOutlined,
  RobotOutlined, ArrowLeftOutlined, ThunderboltOutlined,
} from '@ant-design/icons';

const SECTIONS = [
  { id: 'intro', label: 'Introducción', icon: <RobotOutlined /> },
  { id: 'quick-start', label: 'Inicio Rápido', icon: <RocketOutlined /> },
  { id: 'widget-modes', label: 'Modos del Widget', icon: <AppstoreOutlined /> },
  { id: 'customization', label: 'Personalización', icon: <BgColorsOutlined /> },
  { id: 'documents', label: 'Knowledge Base', icon: <FileTextOutlined /> },
  { id: 'authentication', label: 'Autenticación', icon: <KeyOutlined /> },
  { id: 'api-reference', label: 'API Reference', icon: <ApiOutlined /> },
  { id: 'callbacks', label: 'Callbacks', icon: <ThunderboltOutlined /> },
];

const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group rounded-lg overflow-hidden my-4">
      {language && (
        <div className="bg-gray-800 dark:bg-gray-950 px-4 py-2 text-xs text-gray-400 font-mono border-b border-gray-700">
          {language}
        </div>
      )}
      <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 px-4 py-4 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded-md text-gray-300 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
        title="Copiar"
      >
        {copied ? <CheckOutlined className="text-green-400" /> : <CopyOutlined />}
      </button>
    </div>
  );
};

const EndpointBlock: React.FC<{
  method: string;
  path: string;
  description: string;
  headers?: { key: string; value: string; note?: string }[];
  body?: string;
  response?: string;
  curlExample?: string;
}> = ({ method, path, description, headers, body, response, curlExample }) => {
  const methodColors: Record<string, string> = {
    GET: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400',
    POST: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400',
    PUT: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-400',
    DELETE: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400',
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden my-6">
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <span className={`px-2.5 py-1 rounded-md text-xs font-bold font-mono ${methodColors[method] || ''}`}>
          {method}
        </span>
        <code className="text-sm font-mono text-gray-900 dark:text-white">{path}</code>
      </div>
      <div className="px-4 py-3 space-y-3">
        <p className="text-sm text-gray-700 dark:text-gray-300">{description}</p>
        {headers && headers.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Headers</p>
            <div className="space-y-1">
              {headers.map((h) => (
                <div key={h.key} className="flex items-center gap-2 text-sm">
                  <code className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded text-xs font-mono text-gray-800 dark:text-gray-200">{h.key}</code>
                  <span className="text-gray-500 dark:text-gray-400">→</span>
                  <span className="text-gray-700 dark:text-gray-300 text-xs">{h.value}</span>
                  {h.note && <span className="text-gray-400 text-xs">({h.note})</span>}
                </div>
              ))}
            </div>
          </div>
        )}
        {body && (
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Body (JSON)</p>
            <CodeBlock code={body} language="json" />
          </div>
        )}
        {response && (
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Respuesta</p>
            <CodeBlock code={response} language="json" />
          </div>
        )}
        {curlExample && (
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Ejemplo con cURL</p>
            <CodeBlock code={curlExample} language="bash" />
          </div>
        )}
      </div>
    </div>
  );
};

const DocsAssistant: React.FC = () => {
  const [activeSection, setActiveSection] = useState('intro');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const offsets = SECTIONS.map(({ id }) => {
        const el = document.getElementById(id);
        return { id, top: el ? el.getBoundingClientRect().top : Infinity };
      });

      const current = offsets.reduce((closest, section) =>
        section.top <= 120 && section.top > closest.top ? section : closest,
        { id: offsets[0].id, top: -Infinity }
      );

      setActiveSection(current.id);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <img src="/favicon-logo.png" alt="Kubiy" className="w-5 h-5" />
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">Kubiy Cloud</span>
            </Link>
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-700"></div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Documentación</span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden sm:flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <ArrowLeftOutlined />
              Volver al inicio
            </Link>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              {mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className={`
            ${mobileMenuOpen ? 'fixed inset-0 top-16 z-20 bg-white dark:bg-gray-900 p-6' : 'hidden'}
            lg:block lg:w-64 lg:flex-shrink-0 lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] lg:overflow-y-auto lg:py-8
          `}>
            <nav className="space-y-1">
              {SECTIONS.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollTo(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                    activeSection === section.id
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <span className="text-base">{section.icon}</span>
                  {section.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0 py-8 lg:py-12">
            <div className="max-w-3xl">

              {/* ===== INTRODUCCIÓN ===== */}
              <section id="intro" className="mb-16 scroll-mt-24">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full text-xs font-semibold">
                    Documentación
                  </span>
                </div>
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                  Kubiy Assistant
                </h1>
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                  Kubiy Assistant es un chatbot inteligente potenciado por IA que puedes integrar en tu sitio web en minutos.
                  Sube tus documentos, configura la apariencia y tu asistente estará listo para responder preguntas
                  basándose en tu base de conocimientos.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <CodeOutlined className="text-xl text-indigo-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">Fácil Integración</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Una sola línea de código para instalar el widget</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <FileTextOutlined className="text-xl text-indigo-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">Knowledge Base</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Sube PDF, TXT y DOCX para entrenar tu asistente</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <ApiOutlined className="text-xl text-indigo-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">API Completa</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Endpoints REST para integraciones server-to-server</p>
                  </div>
                </div>
              </section>

              {/* ===== INICIO RÁPIDO ===== */}
              <section id="quick-start" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Inicio Rápido</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Integra tu asistente en tu sitio web en 3 sencillos pasos:
                </p>

                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full flex items-center justify-center text-sm font-bold">1</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Crea tu asistente</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Desde el panel de Kubiy Cloud, ve a <strong>Chatbot IA</strong> y crea un nuevo asistente.
                        Elige tu plan y configura el nombre y la descripción.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full flex items-center justify-center text-sm font-bold">2</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Sube tus documentos</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        En la pestaña <strong>Documentos</strong>, sube archivos PDF, TXT o DOCX.
                        Estos alimentan la base de conocimientos que el asistente usará para responder.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full flex items-center justify-center text-sm font-bold">3</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Instala el widget</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        Copia el snippet de instalación y pégalo antes del cierre de <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">&lt;/body&gt;</code> en tu HTML:
                      </p>
                      <CodeBlock
                        language="html"
                        code={`<script\n  src="https://xxxxxx/widget/kubiy-assistant.min.js"\n  data-api-key="kby_chat_TU_API_KEY">\n</script>`}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Reemplaza <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded font-mono">kby_chat_TU_API_KEY</code> con la API Key de tu asistente, disponible en la pestaña <strong>Configuración</strong>.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* ===== MODOS DEL WIDGET ===== */}
              <section id="widget-modes" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Modos del Widget</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Elige cómo se presenta el asistente en tu sitio web. Puedes cambiar el modo en cualquier momento
                  desde la configuración del widget en el dashboard.
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-5 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Burbuja Flotante</h3>
                    <p className="text-xs text-gray-400 font-mono mb-2">bubble</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Un botón circular en la esquina inferior derecha. Al hacer clic se abre la ventana del chat.
                      Ideal para la mayoría de sitios web.
                    </p>
                  </div>
                  <div className="p-5 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Sidebar</h3>
                    <p className="text-xs text-gray-400 font-mono mb-2">sidebar</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Un panel lateral deslizable que se abre desde el costado de la pantalla.
                      Útil cuando necesitas más espacio para la conversación.
                    </p>
                  </div>
                  <div className="p-5 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Barra Inferior</h3>
                    <p className="text-xs text-gray-400 font-mono mb-2">bottombar</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Un panel fijo en la parte inferior de la página. Mantiene el chat siempre visible
                      sin obstaculizar la navegación.
                    </p>
                  </div>
                  <div className="p-5 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Inline</h3>
                    <p className="text-xs text-gray-400 font-mono mb-2">inline</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Se integra directamente dentro de un contenedor de tu página.
                      Perfecto para páginas de soporte dedicadas.
                    </p>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Tip:</strong> Para el modo <code className="font-mono">inline</code>, agrega el atributo <code className="font-mono">data-container</code> con el ID del contenedor:
                  </p>
                  <CodeBlock
                    language="html"
                    code={`<script\n  src="https://xxxxxxx/widget/kubiy-assistant.min.js"\n  data-api-key="kby_chat_TU_API_KEY"\n  data-container="mi-chat">\n</script>\n<div id="mi-chat" style="height: 500px"></div>`}
                  />
                </div>
              </section>

              {/* ===== PERSONALIZACIÓN ===== */}
              <section id="customization" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Personalización</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Personaliza la apariencia y el comportamiento de tu asistente desde la pestaña <strong>Configuración</strong> en el dashboard.
                  Todos los cambios se reflejan inmediatamente en el widget.
                </p>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Propiedad</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Tipo</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">botName</code></td>
                        <td className="py-3 px-4 text-gray-500 dark:text-gray-400">string</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Nombre que se muestra en la cabecera del widget</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">primaryColor</code></td>
                        <td className="py-3 px-4 text-gray-500 dark:text-gray-400">string (hex)</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Color principal del widget (ej: <code className="font-mono">#6366f1</code>)</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">welcomeMessage</code></td>
                        <td className="py-3 px-4 text-gray-500 dark:text-gray-400">string</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Primer mensaje que ve el usuario al abrir el chat</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">placeholder</code></td>
                        <td className="py-3 px-4 text-gray-500 dark:text-gray-400">string</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Texto placeholder del campo de entrada</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">widgetMode</code></td>
                        <td className="py-3 px-4 text-gray-500 dark:text-gray-400">string</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400"><code className="font-mono">bubble</code> | <code className="font-mono">sidebar</code> | <code className="font-mono">bottombar</code> | <code className="font-mono">inline</code></td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">systemPrompt</code></td>
                        <td className="py-3 px-4 text-gray-500 dark:text-gray-400">string</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Instrucciones de comportamiento para la IA (máx 1000 caracteres)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Ejemplo de System Prompt</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Las instrucciones del asistente le indican a la IA cómo comportarse. Este texto no es visible para el usuario final.
                  </p>
                  <CodeBlock
                    language="text"
                    code={`Eres un agente de ventas de la empresa XYZ.\nSiempre responde de forma amable y profesional.\nOfrece los productos del catálogo cuando sea relevante.\nSi no sabes la respuesta, sugiere contactar a soporte@xyz.com`}
                  />
                </div>
              </section>

              {/* ===== KNOWLEDGE BASE ===== */}
              <section id="documents" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Knowledge Base</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  La base de conocimientos es lo que permite a tu asistente dar respuestas contextualizadas.
                  Sube documentos desde el dashboard y el asistente los procesará automáticamente.
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Formatos soportados</h3>
                <div className="flex gap-3 mb-6">
                  <span className="px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm font-medium border border-red-200 dark:border-red-800">.pdf</span>
                  <span className="px-3 py-1.5 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium border border-gray-200 dark:border-gray-700">.txt</span>
                  <span className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-lg text-sm font-medium border border-blue-200 dark:border-blue-800">.docx</span>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Límites</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Restricción</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Valor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      <tr>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Tamaño máximo por archivo</td>
                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">25 MB</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Documentos</td>
                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">Según plan contratado</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Sincronización</td>
                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">Automática tras subida o eliminación</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mt-6 mb-3">Gestión de documentos</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Desde el dashboard puedes gestionar todos tus documentos:
                </p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Subir documentos arrastrando archivos o seleccionándolos</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Ver la lista completa con nombre, tamaño y fecha</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Eliminar documentos individuales</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Sincronización automática de la Knowledge Base</li>
                </ul>

                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>Nota:</strong> Después de subir o eliminar un documento, la sincronización de la Knowledge Base
                    se ejecuta automáticamente y puede tardar unos segundos. Durante este proceso, las respuestas
                    del asistente podrían no reflejar los últimos cambios.
                  </p>
                </div>
              </section>

              {/* ===== AUTENTICACIÓN ===== */}
              <section id="authentication" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Autenticación</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Kubiy Assistant usa dos tipos de claves para diferentes escenarios de uso:
                </p>

                <div className="space-y-6">
                  <div className="p-5 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded-md text-xs font-bold">PÚBLICA</span>
                      <h3 className="font-semibold text-gray-900 dark:text-white">API Key</h3>
                      <code className="text-xs text-gray-400 font-mono">kby_chat_...</code>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Se usa en el widget embebido en tu sitio web. Es segura para exposición en el frontend
                      ya que solo funciona desde navegadores (requiere header <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-xs font-mono">Origin</code>).
                    </p>
                    <CodeBlock
                      language="html"
                      code={`<!-- Se incluye como data-attribute en el script -->\n<script\n  src="https://xxxxxxx/widget/kubiy-assistant.min.js"\n  data-api-key="kby_chat_xxxxxxxxxxxx">\n</script>`}
                    />
                  </div>

                  <div className="p-5 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2.5 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 rounded-md text-xs font-bold">PRIVADA</span>
                      <h3 className="font-semibold text-gray-900 dark:text-white">Secret Key</h3>
                      <code className="text-xs text-gray-400 font-mono">kby_secret_...</code>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Se usa para integraciones server-to-server. <strong>Nunca la expongas en el frontend.</strong> Se
                      envía en el header <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">x-api-key</code>.
                      Funciona desde cualquier origen (no requiere header Origin).
                    </p>
                    <CodeBlock
                      language="bash"
                      code={`curl -X POST https://api.kubiy.cloud/v1/assistant/chat \\\n  -H "Content-Type: application/json" \\\n  -H "x-api-key: kby_secret_xxxxxxxxxxxx" \\\n  -d '{"message": "¿Cuáles son sus horarios?"}'`}
                    />
                    <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-xs text-red-700 dark:text-red-400">
                        <strong>Importante:</strong> La Secret Key solo se muestra una vez al generarla. Si la pierdes,
                        deberás generar una nueva desde el dashboard (la anterior se invalidará automáticamente).
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* ===== API REFERENCE ===== */}
              <section id="api-reference" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">API Reference</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-8">
                  Estos endpoints te permiten integrar el chat y la subida de documentos directamente en tu aplicación.
                </p>

                {/* Chat Endpoint */}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Enviar Mensaje</h3>
                <EndpointBlock
                  method="POST"
                  path="/assistant/chat"
                  description="Envía un mensaje al asistente y recibe una respuesta basada en la Knowledge Base. Se puede autenticar de dos formas: con apiKey en el body (desde navegador) o con Secret Key en el header (server-to-server)."
                  headers={[
                    { key: 'Content-Type', value: 'application/json' },
                    { key: 'x-api-key', value: 'kby_secret_...', note: 'solo server-to-server' },
                  ]}
                  body={`{\n  "message": "¿Cuáles son sus horarios de atención?",\n  "apiKey": "kby_chat_xxxxxxxxxxxx"\n}`}
                  response={`{\n  "answer": "Nuestros horarios de atención son de Lunes a Sábado de 10:00 AM a 8:00 PM y Domingos de 11:00 AM a 6:00 PM.",\n  "sources": [\n    {\n       "name": "faq.txt"\n    }\n  ]\n}`}
                  curlExample={`# Desde servidor (con Secret Key)\ncurl -X POST https://api.kubiy.cloud/v1/assistant/chat \\\n  -H "Content-Type: application/json" \\\n  -H "x-api-key: kby_secret_xxxxxxxxxxxx" \\\n  -d '{"message": "¿Cuáles son sus horarios?"}'\n\n# Desde navegador (con API Key, requiere header Origin)\nfetch("https://api.kubiy.cloud/v1/assistant/chat", {\n  method: "POST",\n  headers: { "Content-Type": "application/json" },\n  body: JSON.stringify({\n    apiKey: "kby_chat_xxxxxxxxxxxx",\n    message: "¿Cuáles son sus horarios?"\n  })\n})`}
                />

                <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg my-4">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Notas sobre autenticación del chat:</p>
                  <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                    <li>• <strong>Widget/Navegador:</strong> envía <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs font-mono">apiKey</code> en el body. Requiere que el request tenga header <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs font-mono">Origin</code> (lo añade el navegador automáticamente).</li>
                    <li>• <strong>Server-to-server:</strong> envía la Secret Key en el header <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs font-mono">x-api-key</code>. No requiere <code className="font-mono">apiKey</code> en el body ni header Origin.</li>
                  </ul>
                </div>

                {/* Códigos de error */}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mt-8 mb-2">Códigos de Error</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Código</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">400</code></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Mensaje vacío o demasiado largo (máx 2000 caracteres)</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">401</code></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">API Key o Secret Key inválida o no proporcionada</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">403</code></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">El servicio no está activo</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">429</code></td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Límite de mensajes del plan alcanzado o throttling</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Upload Endpoint */}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mt-10 mb-2">Subir Documento</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  La subida de documentos es un proceso de dos pasos: primero solicitas una URL pre-firmada,
                  luego subes el archivo directamente a esa URL.
                </p>

                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Paso 1: Solicitar URL de subida</h4>
                <EndpointBlock
                  method="POST"
                  path="/assistant/upload"
                  description="Solicita una URL pre-firmada de S3 para subir un documento a la Knowledge Base."
                  headers={[
                    { key: 'Content-Type', value: 'application/json' },
                    { key: 'Authorization', value: 'Bearer {jwt_token}', note: 'token de sesión' },
                  ]}
                  body={`{\n  "serviceId": "svc-xxxxxxxxxxxx",\n  "filename": "manual-producto.pdf",\n  "fileSize": 1048576\n}`}
                  response={`{\n  "s3Key": "services/svc-xxx/documents/manual-producto.pdf",\n  "contentType": "application/pdf",\n  "expiresIn": 3600,\n  "filename": "manual-producto.pdf"\n}`}
                />

                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Paso 2: Subir el archivo</h4>
                <CodeBlock
                  language="bash"
                  code={`# Sube el archivo a la URL pre-firmada obtenida en el paso 1\ncurl -X PUT "URL_PREFIRMADA_DEL_PASO_1" \\\n  -H "Content-Type: application/pdf" \\\n  --data-binary @manual-producto.pdf`}
                />

                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg mt-4">
                  <p className="text-sm text-green-800 dark:text-green-300">
                    <strong>Automático:</strong> Después de subir el archivo, la Knowledge Base se sincroniza automáticamente.
                    No necesitas hacer ninguna acción adicional.
                  </p>
                </div>
              </section>

              {/* ===== CALLBACKS ===== */}
              <section id="callbacks" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Callbacks</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  El widget expone el objeto global <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">window.kubiy_assistant</code> que
                  permite ejecutar lógica personalizada cada vez que la IA responde. Esto te permite accionar cosas
                  en tu sitio web con base en la respuesta del asistente.
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">onResponse</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Se ejecuta automáticamente cada vez que el asistente genera una respuesta.
                  Recibe un objeto con la respuesta, las fuentes consultadas y el mensaje original del usuario.
                </p>

                <div className="overflow-x-auto mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Propiedad</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Tipo</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">answer</code></td>
                        <td className="py-3 px-4 text-gray-500 dark:text-gray-400">string</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">La respuesta generada por la IA</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">sources</code></td>
                        <td className="py-3 px-4 text-gray-500 dark:text-gray-400">array</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Documentos de la Knowledge Base utilizados para generar la respuesta</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">message</code></td>
                        <td className="py-3 px-4 text-gray-500 dark:text-gray-400">string</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">El mensaje original que envió el usuario</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Uso básico</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Define la función <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-xs font-mono">onResponse</code> en
                  tu sitio web. El widget la ejecutará cada vez que la IA genere una respuesta:
                </p>
                <CodeBlock
                  language="html"
                  code={`<!-- 1. Define el callback ANTES o DESPUÉS de cargar el widget -->\n<script>\n  kubiy_assistant.onResponse = function(data) {\n    console.log("Respuesta:", data.answer);\n    console.log("Pregunta:", data.message);\n    console.log("Fuentes:", data.sources);\n  };\n</script>\n\n<!-- 2. Carga el widget -->\n<script\n  src="https://dxy0mpvb3evn5.cloudfront.net/widget/kubiy-assistant.min.js"\n  data-api-key="kby_chat_TU_API_KEY">\n</script>`}
                />

                <h3 className="font-semibold text-gray-900 dark:text-white mt-8 mb-3">Ejemplos prácticos</h3>
                <div className="space-y-4">
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Mostrar la respuesta en otro lugar de la página</h4>
                    <CodeBlock
                      language="javascript"
                      code={`kubiy_assistant.onResponse = function(data) {\n  document.getElementById("mi-resultado").innerText = data.answer;\n};`}
                    />
                  </div>

                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Enviar analíticas a tu backend</h4>
                    <CodeBlock
                      language="javascript"
                      code={`kubiy_assistant.onResponse = function(data) {\n  fetch("/api/analytics", {\n    method: "POST",\n    headers: { "Content-Type": "application/json" },\n    body: JSON.stringify({\n      question: data.message,\n      answer: data.answer,\n      timestamp: new Date().toISOString()\n    })\n  });\n};`}
                    />
                  </div>

                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Accionar un flujo según la respuesta</h4>
                    <CodeBlock
                      language="javascript"
                      code={`kubiy_assistant.onResponse = function(data) {\n  // Si la IA menciona el catálogo, abrir modal\n  if (data.answer.includes("catálogo")) {\n    openCatalogModal();\n  }\n\n  // Si menciona contacto, mostrar formulario\n  if (data.answer.includes("contactar")) {\n    showContactForm();\n  }\n};`}
                    />
                  </div>
                </div>

                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Tip:</strong> El callback se ejecuta de forma segura — si tu función lanza un error,
                    no afectará el funcionamiento del widget. Los errores se registran en la consola del navegador.
                  </p>
                </div>
              </section>
            </div>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-gray-800 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            &copy; {new Date().getFullYear()} Kubiy Cloud. Todos los derechos reservados.
          </span>
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/" className="hover:text-gray-900 dark:hover:text-white transition-colors">kubiy.cloud</Link>
            <span>&middot;</span>
            <a href="#" className="hover:text-gray-900 dark:hover:text-white transition-colors">Términos</a>
            <span>&middot;</span>
            <a href="#" className="hover:text-gray-900 dark:hover:text-white transition-colors">Privacidad</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DocsAssistant;